import React, { useState, useEffect } from 'react';
import { Transition, Dialog, DialogPanel, TransitionChild } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCookieBite,
  faTimes,
  faShieldAlt,
  faChartBar,
  faBullhorn
} from '@fortawesome/free-solid-svg-icons';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consentData = localStorage.getItem('site_cookie_consent');

    if (!consentData) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      try {
        const parsed = JSON.parse(consentData);
        setPreferences(parsed);
      } catch (e) {}
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(
      'site_cookie_consent',
      JSON.stringify({
        ...prefs,
        timestamp: new Date().toISOString()
      })
    );
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    const allEnabled = { necessary: true, analytics: true, marketing: true };
    setPreferences(allEnabled);
    saveConsent(allEnabled);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {/* --- BANNER --- */}
      <Transition
        show={showBanner && !showSettings}
        enter="transition ease-out duration-500 transform"
        enterFrom="translate-y-full opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="transition ease-in duration-300 transform"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="translate-y-full opacity-0">
        <div className="fixed bottom-0 left-0 right-0 z-[9990] p-4 md:p-6 flex justify-center pointer-events-none">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 max-w-4xl w-full pointer-events-auto flex flex-col md:flex-row items-center gap-6 p-6 relative">
            {/* Icon */}
            <div className="hidden md:flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full shrink-0">
              <FontAwesomeIcon icon={faCookieBite} className="text-primary text-xl" />
            </div>

            {/* Text Content */}
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-lg font-bold text-gray-800 mb-1">We value your privacy</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                We use cookies to improve your experience. Some are necessary, while others help us
                analyze traffic and serve personalized content.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 shrink-0 w-full md:w-auto">
              <button
                onClick={() => setShowSettings(true)}
                className="flex-1 md:flex-none px-6 py-2.5 rounded-full border border-gray-300 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors">
                Cookie Settings
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 md:flex-none px-6 py-2.5 rounded-full bg-primary text-white font-medium text-sm shadow-md hover:bg-secondary-dark transition-all transform hover:scale-105">
                Accept All
              </button>
            </div>
          </div>
        </div>
      </Transition>

      {/* --- SETTINGS MODAL --- */}
      <Transition appear show={showSettings} as={React.Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={() => setShowSettings(false)}>
          <TransitionChild
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95">
                <DialogPanel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  {/* Header */}
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <FontAwesomeIcon icon={faCookieBite} className="text-primary" />
                      Cookie Preferences
                    </h3>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="text-gray-400 hover:text-gray-600">
                      <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                    </button>
                  </div>

                  <p className="text-sm text-gray-500 mb-6">
                    Manage your cookie preferences here. "Necessary" cookies cannot be disabled as
                    they are required for the website to function properly.
                  </p>

                  {/* Options List */}
                  <div className="space-y-4 mb-8">
                    {/* 1. Necessary */}
                    <div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-gray-50">
                      <div className="flex items-start gap-3">
                        <FontAwesomeIcon icon={faShieldAlt} className="text-gray-400 mt-1" />
                        <div>
                          <p className="font-semibold text-gray-700 text-sm">Strictly Necessary</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Required for basic site functionality.
                          </p>
                        </div>
                      </div>
                      <div className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded">
                        Always Active
                      </div>
                    </div>

                    {/* 2. Analytics */}
                    <div
                      className="flex items-center justify-between p-4 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => togglePreference('analytics')}>
                      <div className="flex items-start gap-3">
                        <FontAwesomeIcon
                          icon={faChartBar}
                          className={`mt-1 ${
                            preferences.analytics ? 'text-primary' : 'text-gray-400'
                          }`}
                        />
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">
                            Analytics & Performance
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Help us understand how visitors interact with the website.
                          </p>
                        </div>
                      </div>
                      {/* Toggle Switch UI */}
                      <div
                        className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${
                          preferences.analytics ? 'bg-primary' : 'bg-gray-300'
                        }`}>
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${
                            preferences.analytics ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </div>
                    </div>

                    {/* 3. Marketing */}
                    <div
                      className="flex items-center justify-between p-4 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => togglePreference('marketing')}>
                      <div className="flex items-start gap-3">
                        <FontAwesomeIcon
                          icon={faBullhorn}
                          className={`mt-1 ${
                            preferences.marketing ? 'text-primary' : 'text-gray-400'
                          }`}
                        />
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">
                            Marketing & Targeting
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Used to deliver relevant ads and track effectiveness.
                          </p>
                        </div>
                      </div>
                      {/* Toggle Switch UI */}
                      <div
                        className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${
                          preferences.marketing ? 'bg-primary' : 'bg-gray-300'
                        }`}>
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${
                            preferences.marketing ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Footer Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="flex-1 py-2.5 rounded-lg border border-primary text-primary font-medium text-sm hover:bg-primary/5 transition-colors">
                      Accept All
                    </button>
                    <button
                      onClick={handleSavePreferences}
                      className="flex-1 py-2.5 rounded-lg bg-primary text-white font-medium text-sm shadow-sm hover:bg-secondary-dark transition-colors">
                      Save Preferences
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CookieConsent;
